import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ForgotPasswordSchema, TForgotPassword } from '@onesource/schemas';
import { useSendPasswordResetMutation } from '@onesource/services';
import { AxiosError } from 'axios';
import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdMarkEmailRead, MdOutlineSend } from 'react-icons/md';

const getErrorMessage = (error: Error): string => {
  if (error instanceof AxiosError && error.response?.data?.message) {
    return error.response.data.message;
  }
  return 'Failed to send password reset email';
};

export interface ForgotPasswordFormProps {
  /** URL Supabase will redirect the user to after clicking the email link */
  passwordResetRedirectUrl?: string;
  /** URL for the "Back to Login" link (default: '/auth/login') */
  loginUrl?: string;
  /** Custom title (default: "Forgot Password") */
  title?: string;
  /** Callback when the reset email is sent successfully */
  onSuccess?: () => void;
  /** Callback when an error occurs */
  onError?: (error: Error) => void;
  /** Custom styles for the form card */
  sx?: SxProps<Theme>;
}

export const ForgotPasswordForm: FC<ForgotPasswordFormProps> = ({
  passwordResetRedirectUrl,
  loginUrl = '/auth/login',
  title = 'Forgot Password',
  onSuccess,
  onError,
  sx,
}) => {
  const mutation = useSendPasswordResetMutation();

  const { control, handleSubmit, watch } = useForm<TForgotPassword>({
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: { email: '' },
    mode: 'all',
  });

  const watchedEmail = watch('email');

  const onSubmit = handleSubmit(({ email }) => {
    mutation.mutate(
      { email, redirectTo: passwordResetRedirectUrl },
      {
        onSuccess: () => onSuccess?.(),
        onError: err => onError?.(err instanceof Error ? err : new Error(String(err))),
      },
    );
  });

  // Success state
  if (mutation.isSuccess) {
    return (
      <Box sx={{ textAlign: 'center', ...sx }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            mx: 'auto',
            mb: 2.5,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: theme => `${theme.palette.success.main}1F`,
            color: 'success.main',
          }}
        >
          <MdMarkEmailRead size={28} />
        </Box>

        <Typography variant="h5" fontWeight={700} mb={1}>
          Check Your Email
        </Typography>
        <Typography color="text.secondary" mb={0.5}>
          We sent a password reset link to:
        </Typography>
        <Typography fontWeight={600} mb={4}>
          {watchedEmail}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Click the link in the email to reset your password.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If you don't see it in your inbox, please check your Spam or Updates folder (in Gmail) and mark the email as
            "Not Spam" so future emails arrive in your inbox.
          </Typography>
        </Box>

        <Link href={loginUrl} sx={{ fontWeight: 600 }}>
          Back to Login
        </Link>
      </Box>
    );
  }

  return (
    <Box sx={sx}>
      <Typography variant="h5" textAlign="center" fontWeight={600} mb={1}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" mb={4}>
        Enter your email address and we'll send you a link to reset your password.
      </Typography>

      <Box component="form" onSubmit={onSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Controller
          name="email"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              label="Email"
              type="email"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              autoComplete="email"
              autoFocus
            />
          )}
        />

        {mutation.isError && (
          <Typography color="error" variant="body2">
            {getErrorMessage(mutation.error)}
          </Typography>
        )}

        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={mutation.isPending}
          endIcon={!mutation.isPending ? <MdOutlineSend /> : undefined}
          sx={{ mt: 1 }}
        >
          {mutation.isPending ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
        </Button>
      </Box>

      <Box mt={3} textAlign="center">
        <Link href={loginUrl} sx={{ fontWeight: 600, fontSize: '14px' }}>
          Back to Login
        </Link>
      </Box>
    </Box>
  );
};
